import { useState, useCallback, useRef, useId, useEffect } from 'react';
import { useClickOutsideWithGlobal } from '@/shared/hooks/useClickOutside';
import { DropdownOption, DropdownRef } from '@/shared/types/dropdown';
import { InputRef } from '@/shared/types/input';

// Sistema global para manejar dropdowns abiertos
const openDropdowns = new Set<string>();

// Función para cerrar todos los dropdowns excepto el especificado
const closeOtherDropdowns = (excludeId: string) => {
  openDropdowns.forEach((id) => {
    if (id !== excludeId) {
      const event = new CustomEvent('close-dropdown', { detail: { id } });
      document.dispatchEvent(event);
    }
  });
};

interface UseDropdownProps {
  value?: string | number | (string | number)[];
  defaultValue?: string | number | (string | number)[];
  options: DropdownOption[];
  valueName?: string;
  idName?: string;
  onValueChange?: (value: string | number | (string | number)[]) => void;
  onOptionSelect?: (option: DropdownOption) => void;
  searchable?: boolean;
  closeOnClickOutside?: boolean;
  dropdownId?: string;
  disabled?: boolean;
  multiple?: boolean;
  maxSelectedItems?: number;
  absolute?: boolean;
  /** Cuando se define, el menú usa este z-index en lugar del por defecto (p. ej. en celdas de tabla para quedar bajo columnas sticky). */
  popoverZIndex?: number;
  onOpenChange?: (isOpen: boolean) => void; // Nuevo callback
  withCheckbox?: boolean;
  /** No permitir quitar selección desde el menú (solo añadir). */
  preventDeselectFromOptionsList?: boolean;
}

export const useDropdown = ({
  value,
  defaultValue,
  options,
  valueName = 'label',
  idName = 'id',
  onValueChange,
  onOptionSelect,
  searchable = false,
  closeOnClickOutside = true,
  dropdownId,
  disabled = false,
  multiple = false,
  maxSelectedItems,
  absolute = false,
  popoverZIndex,
  onOpenChange,
  withCheckbox = false,
  preventDeselectFromOptionsList = false,
}: UseDropdownProps) => {
  // Generar ID único para este dropdown
  const uniqueId = useId();
  const dropdownUniqueId = dropdownId || `dropdown-${uniqueId}`;

  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [position, setPosition] = useState<'bottom' | 'top'>('bottom');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const inputRef = useRef<InputRef>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Función para normalizar valores a array
  const normalizeToArray = useCallback(
    (val: string | number | (string | number)[] | undefined): (string | number)[] => {
      if (val === undefined || val === null) return [];
      if (Array.isArray(val)) return val;
      return [val];
    },
    []
  );

  // Función para normalizar valores a single value
  const normalizeToSingle = useCallback(
    (val: string | number | (string | number)[] | undefined): string | number => {
      if (val === undefined || val === null || val === '') return '';
      if (Array.isArray(val)) return val[0] || '';
      return val;
    },
    []
  );

  // Función para comparar valores de manera flexible (maneja strings y números)
  const compareValues = useCallback((val1: string | number, val2: string | number): boolean => {
    // Si ambos son iguales estrictamente, retornar true
    if (val1 === val2) return true;

    // Convertir ambos a string y comparar
    if (String(val1) === String(val2)) return true;

    // Convertir ambos a número y comparar (si ambos son números válidos)
    const num1 = Number(val1);
    const num2 = Number(val2);
    if (!isNaN(num1) && !isNaN(num2) && num1 === num2) return true;

    return false;
  }, []);

  // Estado interno para manejar valores controlados y no controlados
  const [internalValue, setInternalValue] = useState<string | number | (string | number)[]>(() => {
    if (multiple) {
      return normalizeToArray(defaultValue);
    } else {
      return normalizeToSingle(defaultValue);
    }
  });

  // Determinar si es controlado
  const isControlled = value !== undefined;

  // Valor actual
  const selectedValue = isControlled ? value : internalValue;

  // Función para actualizar el valor
  const setValue = useCallback(
    (newValue: string | number | (string | number)[]) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [isControlled, onValueChange]
  );

  // Sincronizar estado interno cuando cambie el value externo (para componentes controlados)
  useEffect(() => {
    if (isControlled && value !== undefined) {
      if (multiple) {
        const newArrayValue = normalizeToArray(value);
        setInternalValue((prev) => {
          // Solo actualizar si el array es diferente
          if (JSON.stringify(prev) !== JSON.stringify(newArrayValue)) {
            return newArrayValue;
          }
          return prev;
        });
      } else {
        const newSingleValue = normalizeToSingle(value);
        setInternalValue((prev) => {
          // Solo actualizar si el valor es diferente
          if (prev !== newSingleValue) {
            return newSingleValue;
          }
          return prev;
        });
      }
    }
  }, [value, isControlled, multiple, normalizeToArray, normalizeToSingle]);

  // Obtener las opciones seleccionadas
  const selectedOptions: DropdownOption[] = multiple
    ? options.filter((option) => {
        const optionValue = option[idName] as string | number;
        return normalizeToArray(selectedValue).some((val) => compareValues(val, optionValue));
      })
    : (() => {
        const found = options.find((option) => {
          // Normalizar selectedValue a un valor único (no array) para modo single
          const singleValue = normalizeToSingle(selectedValue);

          // Si el valor está vacío, no hay opción seleccionada
          if (singleValue === '' || singleValue === null || singleValue === undefined) {
            return false;
          }
          // Para la opción por defecto, comparar por id
          if ((option[idName] as number) === -1) {
            return singleValue === -1 || singleValue === '-1';
          }
          // Para opciones normales, comparar por idName usando comparación flexible
          return compareValues(option[idName] as string | number, singleValue);
        });
        return found ? [found] : [];
      })();

  // Filtrar opciones basado en búsqueda y si ya están seleccionadas (para múltiple)
  const filteredOptions = (() => {
    if (!searchable || !searchValue) {
      // Si no hay búsqueda, aplicar lógica original
      return options.filter((option) => {
        if (multiple) {
          const optionValue = option[idName] as string | number;
          const isSelected = normalizeToArray(selectedValue).some((val) =>
            compareValues(val, optionValue)
          );
          if (withCheckbox) {
            return true;
          }
          return !isSelected;
        }

        if ((option[idName] as number) === -1 && (selectedValue === -1 || selectedValue === '-1')) {
          return false;
        }
        return true;
      });
    }

    // LÓGICA DE BÚSQUEDA CORREGIDA
    const searchTerm = searchValue.toLowerCase();

    // Primero filtrar solo las opciones que coinciden
    const matchingOptions = options.filter((option) => {
      // Si es un título de grupo, NO incluirlo directamente en la búsqueda
      if (option.isGroupTitle) {
        return false;
      }

      // Si es una opción normal, verificar si coincide con la búsqueda
      const optionValue = option[valueName];
      if (typeof optionValue === 'string') {
        return optionValue.toLowerCase().includes(searchTerm);
      }
      return String(optionValue).toLowerCase().includes(searchTerm);
    });

    // Ahora agregar los grupos correspondientes
    const finalFilteredOptions: DropdownOption[] = [];
    const addedGroups = new Set();

    matchingOptions.forEach((option) => {
      // Buscar el grupo al que pertenece esta opción
      const currentIndex = options.indexOf(option);
      let groupTitle = null;

      // Buscar hacia atrás para encontrar el grupo al que pertenece esta opción
      for (let i = currentIndex - 1; i >= 0; i--) {
        if (options[i].isGroupTitle) {
          groupTitle = options[i];
          break;
        }
      }

      // Si encontramos un grupo y no lo hemos agregado antes, agregarlo
      if (groupTitle && !addedGroups.has(groupTitle[idName])) {
        finalFilteredOptions.push(groupTitle);
        addedGroups.add(groupTitle[idName]);
      }

      // Agregar la opción
      finalFilteredOptions.push(option);
    });

    return finalFilteredOptions;
  })();

  // const filteredOptions = options.filter((option) => {
  //   const matchesSearch = searchable
  //     ? option[valueName].toLowerCase().includes(searchValue.toLowerCase())
  //     : true

  //   if (multiple) {
  //     const isSelected = normalizeToArray(selectedValue).includes(option[idName])

  //     if (withCheckbox) {
  //       return matchesSearch
  //     }
  //     return matchesSearch && !isSelected
  //   }

  //   // Para selección simple, ocultar la opción por defecto si está seleccionada
  //   if (option[idName] === -1 && (selectedValue === -1 || selectedValue === '-1')) {
  //     return false
  //   }

  //   return matchesSearch
  // })

  // Función para obtener el elemento DOM del input
  const getInputElement = useCallback(() => {
    // Buscar primero un input, si no existe, buscar el div personalizado
    const inputElement = containerRef.current?.querySelector('input') as HTMLInputElement;
    if (inputElement) return inputElement;

    // Para modo tags, buscar el div personalizado con el atributo data
    const customDiv = containerRef.current?.querySelector(
      '[data-dropdown-input="true"]'
    ) as HTMLElement;
    return customDiv;
  }, []);

  // Calcular posición del dropdown con ID único
  const calculatePosition = useCallback(() => {
    const inputElement = getInputElement();
    if (!inputElement) return;

    // Esperar un frame para asegurar que el DOM esté actualizado
    requestAnimationFrame(() => {
      const inputRect = inputElement.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;

      // Obtener la altura real del dropdown si está disponible
      let dropdownHeight = 0;
      if (dropdownRef.current) {
        dropdownHeight = dropdownRef.current.offsetHeight;
      }

      // Si no tenemos altura real, usar una estimación temporal
      if (dropdownHeight === 0) {
        dropdownHeight = Math.min(200, filteredOptions.length * 40);
      }

      const spaceBelow = windowHeight - inputRect.bottom;
      const spaceAbove = inputRect.top;

      const newPosition = spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? 'top' : 'bottom';
      setPosition(newPosition);

      let top: number;
      let left: number;
      const width = inputRect.width;

      if (!absolute) {
        // Posicionamiento fijo (portal) - comportamiento original
        if (newPosition === 'bottom') {
          top = inputRect.bottom + 4;
        } else {
          top = inputRect.top - dropdownHeight - 4;
        }
        left = inputRect.left;
      } else {
        // Posicionamiento absoluto (relativo al contenedor)
        const containerRect = containerRef.current?.getBoundingClientRect();
        if (!containerRect) return;

        const relativeTop = inputRect.top - containerRect.top;
        const relativeLeft = inputRect.left - containerRect.left;

        if (newPosition === 'bottom') {
          top = relativeTop + inputRect.height + 4;
        } else {
          top = relativeTop - dropdownHeight - 4;
        }
        left = relativeLeft;
      }

      // Generar z-index: si popoverZIndex está definido (p. ej. en tabla), usarlo para quedar bajo columnas sticky
      const zIndexBase = popoverZIndex !== undefined ? popoverZIndex : absolute ? 9999 : 1100;
      const zIndexOffset =
        popoverZIndex !== undefined ? 0 : parseInt(dropdownUniqueId.replace(/\D/g, '')) || 0;
      const uniqueZIndex = zIndexBase + (zIndexOffset % 100);

      // Determinar el tipo de posición final
      const finalPosition = absolute ? ('absolute' as const) : ('fixed' as const);

      const baseStyle = {
        position: finalPosition,
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        /** Igual que `width`: el panel de la lista nunca debe superar el ancho medido del trigger. */
        maxWidth: `${width}px`,
        boxSizing: 'border-box' as const,
        zIndex: uniqueZIndex,
      };

      setDropdownStyle(baseStyle);
    });
  }, [getInputElement, filteredOptions.length, dropdownUniqueId, absolute, popoverZIndex]);

  // Función para hacer scroll hasta la opción seleccionada
  const scrollToSelectedOption = useCallback(() => {
    if (!dropdownRef.current || !selectedOptions.length) {
      return;
    }

    const dropdownElement = dropdownRef.current;
    const scrollContainer = dropdownElement.querySelector('.ui-overflow-y-auto') as HTMLElement;

    if (!scrollContainer) {
      return;
    }

    // Para múltiple, hacer scroll hasta la primera opción seleccionada
    const firstSelectedOption = selectedOptions[0];
    if (!firstSelectedOption) return;

    const selectedElement = scrollContainer.querySelector(
      `[data-option-value="${firstSelectedOption[idName]}"]`
    ) as HTMLElement;

    if (!selectedElement) {
      return;
    }

    // Calcular la posición para centrar la opción seleccionada
    const containerHeight = scrollContainer.clientHeight;
    const elementTop = selectedElement.offsetTop;
    const elementHeight = selectedElement.clientHeight;
    const scrollTop = elementTop - containerHeight / 2 + elementHeight / 2;

    // Hacer scroll suave hasta la posición
    scrollContainer.scrollTo({
      top: Math.max(0, scrollTop),
      behavior: 'smooth',
    });
  }, [selectedOptions, idName]);

  // Manejar apertura/cierre del dropdown
  const toggleDropdown = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }

    if (disabled) {
      return;
    }

    if (!isOpen) {
      // Cerrar otros dropdowns antes de abrir este
      closeOtherDropdowns(dropdownUniqueId);

      setSearchValue('');
      setIsClosing(false);
      // Calcular posición primero
      calculatePosition();
      // Luego abrir el dropdown
      setTimeout(() => {
        setIsOpen(true);
        openDropdowns.add(dropdownUniqueId);
        // Recalcular posición con altura real después de que se renderice
        setTimeout(() => {
          calculatePosition();
          // Hacer scroll hasta la opción seleccionada después de que se abra
          setTimeout(() => {
            scrollToSelectedOption();
          }, 50);
        }, 50);
      }, 50);
    } else {
      closeDropdown();
    }
  };

  // Función para cerrar el dropdown con animación
  const closeDropdown = useCallback(() => {
    setIsClosing(true);
    // Esperar a que termine la animación antes de ocultar
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
      openDropdowns.delete(dropdownUniqueId);
    }, 150);
  }, [dropdownUniqueId]);

  // Manejar selección de opción
  const handleOptionSelect = (option: DropdownOption, event?: React.MouseEvent) => {
    event?.stopPropagation?.();

    if (option.disabled) return;

    if (multiple) {
      const currentValues = normalizeToArray(selectedValue);
      const optionValue = option[idName] as string | number;

      // Verificar límite máximo
      if (
        maxSelectedItems &&
        currentValues.length >= maxSelectedItems &&
        !currentValues.includes(optionValue)
      ) {
        return;
      }

      if (currentValues.includes(optionValue)) {
        if (preventDeselectFromOptionsList) {
          return;
        }
        setValue(currentValues.filter((v) => v !== optionValue));
      } else {
        const newValues = [...currentValues, optionValue];
        setValue(newValues);
      }
    } else {
      // Para la opción por defecto, usar el valor real (-1) en lugar de la etiqueta
      const newValue = (option[idName] as number) === -1 ? -1 : (option[idName] as string | number);

      setValue(newValue);
      setSearchValue('');
      closeDropdown();
    }

    onOptionSelect?.(option);
  };

  // Manejar búsqueda
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const clearSearch = useCallback(() => {
    setSearchValue('');
  }, []);

  // Manejar limpieza desde el campo (X) o equivalente
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setValue(multiple ? [] : '');
    setSearchValue('');
  };

  // Notificar cambios en el estado isOpen
  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  // Usar el hook mejorado de clic fuera - solo cuando esté completamente abierto.
  // Siempre pasamos containerRef (trigger) para que clics en el selector no se consideren "fuera":
  // así el cierre lo hace solo toggleDropdown (onClick/onClickExtraSlot del Input) y se evitan
  // races con modales/portales (p. ej. clic en la chevron derecha).
  useClickOutsideWithGlobal({
    isActive: isOpen,
    disabled: disabled,
    elementRef: dropdownRef,
    containerRef,
    elementId: dropdownUniqueId,
    elementType: 'dropdown',
    closeOnClickOutside,
    disableSameTypeCheck: absolute,
    onOutsideClick: () => {
      setTimeout(() => {
        closeDropdown();
      }, 0);
    },
  });

  // Listener para cerrar todos los dropdowns (usado por componentes padre como Popup)
  useEffect(() => {
    const handleCloseAllDropdowns = () => {
      if (isOpen) {
        closeDropdown();
      }
    };

    document.addEventListener('close-all-dropdowns', handleCloseAllDropdowns);

    return () => {
      document.removeEventListener('close-all-dropdowns', handleCloseAllDropdowns);
    };
  }, [isOpen, closeDropdown]);

  // Recalcular posición cuando cambie el tamaño de la ventana o se haga scroll
  useEffect(() => {
    if (isOpen) {
      const handleResize = () => {
        calculatePosition();
      };

      const handleScroll = () => {
        // Solo recalcular posición si el dropdown está visible
        if (isOpen && !isClosing) {
          calculatePosition();
        }
      };

      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, false);
      document.addEventListener('scroll', handleScroll, false);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, false);
        document.removeEventListener('scroll', handleScroll, false);
      };
    }
  }, [isOpen, calculatePosition, isClosing]);

  return {
    // Estado
    isOpen,
    isClosing,
    searchValue,
    selectedValue,
    selectedOptions,
    filteredOptions,
    position,
    dropdownStyle,
    isControlled,
    multiple,

    // Referencias
    inputRef,
    dropdownRef,
    containerRef,
    dropdownUniqueId,

    // Funciones
    toggleDropdown,
    closeDropdown,
    handleOptionSelect,
    handleSearchChange,
    handleClear,
    clearSearch,
    calculatePosition,
    setValue,
    scrollToSelectedOption,
    normalizeToArray,
  };
};
